import { useGlobalStore } from '../store';
import type { JSApiList } from 'wx-jssdk';
import { useHostEnv } from '../hooks';
import { HostEnv } from '@shared/types';
import { commonApi } from '../apis';
import { WX_AGENT_CONFIG_JSAPI_LIST } from '../config';
import _ from 'lodash';

export interface WxConfigParams {
  beta?: boolean;
  debug?: boolean;
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: JSApiList[];
  openTagList?: string[];
}

export interface AgentConfigParams {
  corpId: string;
  agentId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: JSApiList[];
}

export function checkWxReady() {
  return new Promise((resolve, reject) => {
    wx.ready(() => resolve(true));
    wx.error((error) => reject(error));
  });
}

export const wxConfig = (params: WxConfigParams) =>
  new Promise((resolve, reject) => {
    console.log(params, 'wxConfig');
    if (typeof wx === 'undefined') {
      reject();
    }
    wx.config({
      beta: params.beta,
      debug: params.debug ?? false,
      appId: params.appId,
      timestamp: params.timestamp,
      nonceStr: params.nonceStr,
      signature: params.signature,
      jsApiList: params.jsApiList,
      openTagList: ['wx-open-launch-weapp'],
    });

    checkWxReady()
      .then(() => {
        console.log('wx.config ok');
        resolve(true);
      })
      .catch((error) => {
        console.log(error, 'wx.config error');
        reject(error);
      });
  });

export const agentConfig = (params: AgentConfigParams) =>
  new Promise((resolve, reject) => {
    console.log(params, 'agentConfig');
    if (typeof wx === 'undefined') {
      reject();
    }
    wx.agentConfig({
      corpid: params.corpId,
      agentid: params.agentId,
      timestamp: params.timestamp,
      nonceStr: params.nonceStr,
      signature: params.signature,
      jsApiList: params.jsApiList,
      success: function (res) {
        console.log(res, 'wx.agentConfig ok');
        resolve(res);
      },
      fail: function (res) {
        console.log(res, 'wx.agentConfig error');
        reject(res);
      },
    });
  });

export const wxApiConfig = async (apiName: JSApiList | JSApiList[]) =>
  new Promise(async (resolve, reject) => {
    const globalStore = useGlobalStore();
    const { currentHostEnv } = useHostEnv();
    const jsApiList = typeof apiName === 'string' ? [apiName] : [...apiName];
    console.log('=====> wxConfig start', currentHostEnv);

    // wxConfig api ticket
    const { retdata } = await commonApi.getTicket<any, any>({
      appId: globalStore.appData.appId,
      corpId: currentHostEnv === HostEnv.WX ? globalStore.appData.authCorpId : globalStore.appData.currentCorpId,
      wechatType: currentHostEnv === HostEnv.QYWX ? 1 : 2,
      jsUrl: encodeURIComponent(
        /(iPhone|iPad|iPod|iOS)/i.test(navigator.userAgent) && currentHostEnv === HostEnv.WX
          ? globalStore.firstEntryPageUrl.split('#')[0]
          : location.href.split('#')[0]
      ),
      timestamp: new Date().getTime(),
    });
    console.log(retdata, 'wxConfig getTicket res');

    const wxConfigParams: WxConfigParams = {
      appId: retdata.appId,
      timestamp: retdata.timestamp,
      nonceStr: retdata.noncestr,
      signature: retdata.signature,
      jsApiList,
    };

    if (currentHostEnv === HostEnv.QYWX) {
      wxConfigParams.beta = true;
      wxConfigParams.appId = globalStore.appData.currentCorpId;
    }
    if (currentHostEnv === HostEnv.WX) {
      wxConfigParams.openTagList = ['wx-open-launch-weapp'];
    }

    const wxConfigRes = await wxConfig(wxConfigParams).catch((error) => {
      console.log(error, 'wxConfig call error');
      reject(error);
    });

    console.log('=====> wxConfig end');

    const isHasWxConfigApi = _.intersection(jsApiList, WX_AGENT_CONFIG_JSAPI_LIST).length > 0;
    if (currentHostEnv === HostEnv.QYWX && isHasWxConfigApi && wxConfigRes) {
      console.log('=====> agentConfig start', wxConfigRes);

      const { retdata } = await commonApi.getTicket<any, any>({
        appId: globalStore.appData.appId,
        corpId: globalStore.appData.currentCorpId,
        wechatType: 3,
        jsUrl: encodeURIComponent(location.href.split('#')[0]),
        timestamp: new Date().getTime(),
      });
      console.log(retdata, 'agentConfig getTicket res');

      await agentConfig({
        corpId: globalStore.appData.currentCorpId,
        agentId: retdata.agentId,
        timestamp: retdata.timestamp,
        nonceStr: retdata.noncestr,
        signature: retdata.signature,
        jsApiList,
      }).catch((error) => {
        console.log(error, 'agentConfig call error');
        reject(error);
      });

      console.log('=====> agentConfig end');
    }

    resolve(true);
  });
